/**
 * @fileoverview Hook Data Layer para gestión de topics de conversación de un customer en Inbox.
 * @module useConversationTopicsData
 * @category Hook - Data Layer
 * @since 3.1.0
 * @version 3.1.0
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import customersModelMc from '../../../models/CustomersModelMc';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { useTranslation } from 'react-i18next';
import { isInboxMsChat } from '../../../../services/inbox/inboxMsChatPlatform';

/**
 * @typedef {Object} Topic
 * @property {string|number} id - ID único del topic
 * @property {string} name - Nombre del topic
 * @property {string} [date] - Fecha asociada al topic
 */

/**
 * Hook Data Layer para cargar y gestionar topics de conversación de un customer.
 *
 * - Carga topics paginados
 * - Maneja loading, paginación y errores
 * - Expone handlers para UI y business layer
 *
 * @param {Object} conversation - Objeto de conversación con customerId y platform
 * @returns {Object} Estado y handlers para topics
 * @see useConversationTopicsUI
 */
export default function useConversationTopicsData(conversation) {
  const dispatch = useDispatch();
  const { t } = useTranslation('InboxMC');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showTopicSelector, setShowTopicSelector] = useState(false);
  const [customerTopics, setCustomerTopics] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  // Cargar topics
  const loadData = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const customerData = isInboxMsChat(conversation?.platform)
        ? await customersModelMc.getMicroserviceCustomerTopics({
            chatId: conversation.id,
            platform: conversation.platform,
            page: reset ? 1 : currentPage
          })
        : await customersModelMc.fetchCustomerTopics({
            customerId: conversation.customerId,
            platform: conversation.platform,
            page: reset ? 1 : currentPage
          });
      setTotalPages(customerData.total_pages || 1);
      if (reset || currentPage === 1) {
        setCustomerTopics(customerData.topics || []);
      } else {
        setCustomerTopics((prev) => [...prev, ...(customerData.topics || [])]);
      }
    } catch (error) {
      const errorMessage = error?.message || error || 'Error loading conversation topics';
      dispatch({ type: LOAD_DATA_FAILURE, error: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [conversation?.id, conversation?.customerId, conversation?.platform, currentPage, dispatch]);

  // Efecto: cargar topics al montar o cambiar conversación
  useEffect(() => {
    if (conversation?.customerId && conversation?.platform) {
      setCurrentPage(1);
      setCustomerTopics([]);
      loadData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.customerId, conversation?.platform]);

  // Handler: abrir modal de selector
  const handleOpenTopicSelector = useCallback(() => {
    setShowTopicSelector(true);
  }, []);

  // Handler: cerrar modal y recargar topics
  const handleCloseTopicSelector = useCallback(() => {
    setShowTopicSelector(false);
    setCurrentPage(1);
    setCustomerTopics([]);
    loadData(true);
  }, [loadData]);

  // Handler: eliminar topic
  const handleDeleteTopic = useCallback(async (id) => {
    const ok = window.confirm(
      t('ConversationTopics:conversation_topics.messages.delete_confirmation')
    );
    if (!ok) return;
    try {
      if (isInboxMsChat(conversation?.platform)) {
        await customersModelMc.deleteMicroserviceCustomerTopic({
          chatId: conversation.id,
          platform: conversation.platform,
          topicId: id
        });
      } else {
        await customersModelMc.deleteCustomerTopic({ customerId: conversation.customerId, topicId: id });
      }
      setCustomerTopics((prev) => prev.filter(topic => topic.id !== id));
    } catch (error) {
      const errorMessage = error?.message || error || 'Error deleting conversation topics';
      dispatch({ type: LOAD_DATA_FAILURE, error: errorMessage });
    }
  }, [conversation?.id, conversation?.platform, conversation?.customerId, dispatch, t]);

  // Handler: paginación
  const handleViewMore = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  // Efecto: cargar más topics al cambiar de página
  useEffect(() => {
    if (currentPage > 1) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Memo: hay más páginas
  const viewMore = useMemo(() => currentPage < totalPages, [currentPage, totalPages]);

  return useMemo(() => ({
    loading,
    customerTopics,
    viewMore,
    showTopicSelector,
    handleOpenTopicSelector,
    handleCloseTopicSelector,
    handleDeleteTopic,
    handleViewMore
  }), [loading, customerTopics, viewMore, showTopicSelector, handleOpenTopicSelector, handleCloseTopicSelector, handleDeleteTopic, handleViewMore]);
}
